import { useNavigate } from 'react-router-dom';
import { PageHero, ServiceCard, CTABanner, SectionHeader } from '../components/ui/UI';
import ContactInfo from '../components/ui/ContactInfo';
import { SERVICES } from '../data/services';
import './Jobs.css';

const jobServices = SERVICES.filter(s => s.cat === 'jobs');

const JOB_COUNTRIES = [
  { flag: '🇦🇪', name: 'Dubai / UAE',   jobs: ['Hotel Staff', 'Sales', 'Construction', 'Healthcare'] },
  { flag: '🇬🇧', name: 'UK',            jobs: ['IT', 'Nursing', 'Hospitality', 'Engineering'] },
  { flag: '🇩🇪', name: 'Germany',       jobs: ['Engineering', 'IT', 'Healthcare', 'Skilled Trades'] },
  { flag: '🇸🇬', name: 'Singapore',     jobs: ['Finance', 'IT', 'Hospitality', 'Retail'] },
  { flag: '🇯🇵', name: 'Japan',         jobs: ['Engineering', 'IT', 'Healthcare', 'Manufacturing'] },
  { flag: '🇨🇦', name: 'Canada',        jobs: ['Skilled Trades', 'IT', 'Healthcare', 'Agriculture'] },
  { flag: '🇦🇺', name: 'Australia',     jobs: ['Mining', 'Agriculture', 'IT', 'Healthcare'] },
  { flag: '🇰🇼', name: 'Kuwait / Oman', jobs: ['Oil & Gas', 'Construction', 'Healthcare', 'Hospitality'] },
];

const CRUISE_ROLES = [
  { icon: '🍽️', role: 'Food & Beverage' },
  { icon: '🛏️', role: 'Housekeeping' },
  { icon: '🎭', role: 'Entertainment' },
  { icon: '💆', role: 'Spa & Wellness' },
  { icon: '🛡️', role: 'Security' },
  { icon: '👨‍🍳', role: 'Culinary Arts' },
  { icon: '📋', role: 'Administration' },
  { icon: '⚙️', role: 'Technical / Deck' },
];

export default function Jobs() {
  const navigate = useNavigate();

  const handleInquire = (service) => {
    navigate('/contact', { state: { service } });
  };

  return (
    <div className="page-wrapper">
      <PageHero
        badge="Jobs & Visa"
        title="Work Abroad &"
        highlight="Job Placements"
        subtitle="Verified job placements with full work permit and visa assistance for 20+ countries from Mumbai bombay international worldwide. Also covering Mumbai bombay international worldwide airport jobs, part-time roles, and cruise ship placements."
      />

      {/* Job services grid */}
      <section className="section-pad">
        <div className="container">
          <SectionHeader
            badge="Our Job Services"
            title="Find the Right Opportunity"
            subtitle="From international work visas to local Mumbai bombay international worldwide jobs - we connect you to verified employers across all industries."
          />
          <div className="jobs-services-grid">
            {jobServices.map(s => (
              <ServiceCard key={s.id} service={s} onInquire={handleInquire} />
            ))}
          </div>
        </div>
      </section>

      {/* Work abroad - countries */}
      <section className="section-pad jobs-abroad">
        <div className="container">
          <SectionHeader
            badge="Work Abroad"
            badgeVariant="teal"
            title="Jobs Available in 20+ Countries"
            subtitle="We have verified employer connections in all major countries for candidates in Mumbai bombay international worldwide. Click any country to inquire about available jobs."
          />
          <div className="jobs-countries-grid">
            {JOB_COUNTRIES.map((c, i) => (
              <div
                key={c.name}
                className={`jobs-country-card animate-fade-up delay-${(i % 4) + 1}`}
                onClick={() => handleInquire(`Work Permit & Job Visa Abroad – ${c.name}`)}
              >
                <div className="jobs-country-card__flag">{c.flag}</div>
                <div className="jobs-country-card__name">{c.name}</div>
                <div className="jobs-country-card__roles">
                  {c.jobs.map(j => (
                    <span key={j} className="jobs-country-card__role">{j}</span>
                  ))}
                </div>
                <button className="jobs-country-card__btn">Inquire →</button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Airport jobs */}
      <section className="section-pad jobs-airport">
        <div className="container">
          <div className="jobs-airport__grid">
            <div className="jobs-airport__text">
              <span className="section-badge badge-saffron">Mumbai bombay international worldwide Airport</span>
              <h2 className="jobs-airport__title">Airport Jobs -<br />Mumbai bombay international worldwide & International</h2>
              <p className="jobs-airport__sub">
                Join the aviation industry as an air hostess or airport ground staff. 
                We provide complete training, grooming, and interview preparation to 
                get you placed at Mumbai bombay international worldwide airport and international airports worldwide.
              </p>
              <div className="jobs-airport__roles">
                {['Air Hostess / Cabin Crew', 'Airport Ground Staff', 'Check-in Agent', 'Baggage Handler', 'Security Staff', 'Lounge Executive'].map(r => (
                  <div key={r} className="jobs-airport__role-item">
                    <span className="jobs-airport__role-dot" />
                    {r}
                  </div>
                ))}
              </div>
              <button className="btn btn--primary" onClick={() => handleInquire('Airport Job – Air Hostess / Ground Staff')}>
                Apply for Airport Job
              </button>
            </div>
            <div className="jobs-airport__info-box">
              <h4 className="jobs-airport__info-title">What We Provide</h4>
              {[
                { icon: '📋', title: 'Interview Preparation', desc: 'Mock interviews, personality development, and HR coaching in Mumbai bombay international worldwide.' },
                { icon: '💼', title: 'Grooming & Uniform', desc: 'Professional grooming sessions and uniform guidance for aviation roles.' },
                { icon: '🌐', title: 'International Placement', desc: 'Connections to international airlines and airports globally from Mumbai bombay international worldwide.' },
                { icon: '📜', title: 'Documentation Help', desc: 'Assistance with all paperwork and background checks for your job visa.' },
              ].map(item => (
                <div key={item.title} className="jobs-airport__info-item">
                  <span className="jobs-airport__info-icon">{item.icon}</span>
                  <div>
                    <div className="jobs-airport__info-item-title">{item.title}</div>
                    <div className="jobs-airport__info-item-desc">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Cruise ship jobs */}
      <section className="section-pad jobs-cruise">
        <div className="container">
          <SectionHeader
            badge="Cruise Ship Jobs"
            badgeVariant="gold"
            title="Work on International Cruise Ships"
            subtitle="Join leading cruise lines from Mumbai bombay international worldwide like Royal Caribbean, MSC, Carnival, and more. Earn in USD/EUR with free accommodation and meals on board."
          />
          <div className="jobs-cruise-grid">
            {CRUISE_ROLES.map((r, i) => (
              <div key={r.role} className={`jobs-cruise-card animate-fade-up delay-${(i % 4) + 1}`}>
                <span className="jobs-cruise-card__icon">{r.icon}</span>
                <span className="jobs-cruise-card__role">{r.role}</span>
              </div>
            ))}
          </div>
          <div className="jobs-cruise-benefits">
            {[
              { icon: '💵', text: 'Earn in USD / EUR' },
              { icon: '🛏️', text: 'Free accommodation on ship' },
              { icon: '🍽️', text: 'Free meals included' },
              { icon: '🌍', text: 'Travel the world' },
              { icon: '📈', text: 'Career growth opportunities' },
              { icon: '✈️', text: 'Return airfare often provided' },
            ].map(b => (
              <div key={b.text} className="jobs-cruise-benefit">
                <span>{b.icon}</span>
                <span>{b.text}</span>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <button className="btn btn--secondary" onClick={() => handleInquire('Cruise Ship Job')}>
              Inquire About Cruise Jobs
            </button>
          </div>
        </div>
       </section>

       {/* Contact Section */}
       <section className="section-pad jobs-quick-contact">
         <div className="container">
           <div className="jobs-quick-contact__inner">
             <h2 className="jobs-quick-contact__title">💼 Ready to Start Your International Career?</h2>
             <p className="jobs-quick-contact__sub">Connect with our job placement experts today. We're here to help 24/7!</p>
             <ContactInfo layout="compact" theme="light" />
           </div>
         </div>
       </section>

       <CTABanner
         title="Ready to start your career abroad?"
         sub="Send an inquiry to our Mumbai bombay international worldwide team and our job placement experts will contact you within 24 hours."
       />
    </div>
  );
}
